import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../lib/getApiUrl';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Flag, 
  Clock, 
  AlertTriangle, 
  CheckCircle,
  RefreshCw,
  Filter,
  Search,
  Edit,
  Save,
  X
} from 'lucide-react';

interface ProjectPriority {
  id: string;
  title: string;
  description: string;
  address: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  status: string;
  tags: string[];
  estimatedDuration?: number;
  actualStartDate?: string;
  actualEndDate?: string;
  createdAt: string;
  updatedAt: string;
  homeowner: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

const ProjectPriorityManagement: React.FC = () => {
  const [projects, setProjects] = useState<ProjectPriority[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filterPriority, setFilterPriority] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProject, setEditingProject] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ProjectPriority>>({});

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getApiUrl('/api/admin/projects/priority'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('clerk_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const data = await response.json();
      setProjects(data.projects || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchProjects();
    setRefreshing(false);
  };

  const updateProjectPriority = async (projectId: string, priority: string) => {
    try {
      const response = await fetch(`/api/admin/projects/${projectId}/priority`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('clerk_token')}`,
        },
        body: JSON.stringify({ priority }),
      });

      if (!response.ok) {
        throw new Error('Failed to update project priority');
      }

      await fetchProjects();
      setEditingProject(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update priority');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'HIGH':
        return <Flag className="h-4 w-4 text-orange-500" />;
      case 'MEDIUM':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'LOW':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesPriority = filterPriority === 'ALL' || project.priority === filterPriority;
    const matchesSearch = searchTerm === '' || 
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${project.homeowner.firstName} ${project.homeowner.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesPriority && matchesSearch;
  });

  const priorityCounts = {
    URGENT: projects.filter(p => p.priority === 'URGENT').length,
    HIGH: projects.filter(p => p.priority === 'HIGH').length,
    MEDIUM: projects.filter(p => p.priority === 'MEDIUM').length,
    LOW: projects.filter(p => p.priority === 'LOW').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading project priority data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Project Priority Management</h1>
          <p className="text-gray-600 mt-1">Manage project priorities and track urgent projects</p>
        </div>
        <div className="flex items-center space-x-4">
          <Button
            onClick={refreshData}
            disabled={refreshing}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Priority Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-800">Urgent</p>
                <p className="text-2xl font-bold text-red-900">{priorityCounts.URGENT}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-800">High</p>
                <p className="text-2xl font-bold text-orange-900">{priorityCounts.HIGH}</p>
              </div>
              <Flag className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-800">Medium</p>
                <p className="text-2xl font-bold text-yellow-900">{priorityCounts.MEDIUM}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">Low</p>
                <p className="text-2xl font-bold text-green-900">{priorityCounts.LOW}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Urgent Projects Alert */}
      {priorityCounts.URGENT > 0 && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <strong>{priorityCounts.URGENT} urgent project(s)</strong> require immediate attention.
            <Button variant="link" className="p-0 ml-2 text-red-600">
              View Details
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Error State */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Priorities</option>
                <option value="URGENT">Urgent</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Projects List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredProjects.map((project) => (
          <Card key={project.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between text-sm font-medium">
                <div className="flex items-center space-x-2">
                  {getPriorityIcon(project.priority)}
                  <span className="text-gray-700 truncate">{project.title}</span>
                </div>
                <Badge className={getPriorityColor(project.priority)}>
                  {project.priority}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Project Info */}
              <div>
                <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
                <p className="text-xs text-gray-500 mt-1">{project.address}</p>
              </div>

              {/* Homeowner Info */}
              <div className="text-sm">
                <p className="font-medium text-gray-900">
                  {project.homeowner.firstName} {project.homeowner.lastName}
                </p>
                <p className="text-gray-600">{project.homeowner.email}</p>
              </div>

              {/* Tags */}
              {project.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {project.tags.slice(0, 3).map((tag, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {project.tags.length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{project.tags.length - 3} more
                    </Badge>
                  )}
                </div>
              )}

              {/* Duration Info */}
              {project.estimatedDuration && (
                <div className="text-xs text-gray-500">
                  <span className="font-medium">Estimated Duration:</span> {project.estimatedDuration} days
                </div>
              )}

              {/* Status and Dates */}
              <div className="text-xs text-gray-500 space-y-1">
                <div>
                  <span className="font-medium">Status:</span> {project.status}
                </div>
                <div>
                  <span className="font-medium">Created:</span> {new Date(project.createdAt).toLocaleDateString()}
                </div>
                {project.actualStartDate && (
                  <div>
                    <span className="font-medium">Started:</span> {new Date(project.actualStartDate).toLocaleDateString()}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex space-x-2 pt-2 border-t">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingProject(project.id);
                    setEditForm({ priority: project.priority });
                  }}
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit Priority
                </Button>
              </div>

              {/* Edit Form */}
              {editingProject === project.id && (
                <div className="pt-2 border-t">
                  <div className="space-y-2">
                    <select
                      value={editForm.priority || project.priority}
                      onChange={(e) => setEditForm({ ...editForm, priority: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="URGENT">Urgent</option>
                    </select>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        onClick={() => updateProjectPriority(project.id, editForm.priority!)}
                        className="flex-1"
                      >
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingProject(null);
                          setEditForm({});
                        }}
                        className="flex-1"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredProjects.length === 0 && !loading && (
        <Card>
          <CardContent className="text-center py-12">
            <Flag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Projects Found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || filterPriority !== 'ALL' 
                ? 'No projects match your current filters.'
                : 'No projects available. Projects will appear here once homeowners create them.'
              }
            </p>
            {(searchTerm || filterPriority !== 'ALL') && (
              <Button 
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setFilterPriority('ALL');
                }}
              >
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProjectPriorityManagement;

